(() => {
  const roots = new WeakSet();

  function haversineKm(a, b) {
    const rad = Math.PI / 180;
    const dLat = (b.lat - a.lat) * rad;
    const dLng = (b.lng - a.lng) * rad;
    const s = Math.sin(dLat / 2) ** 2
      + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2;
    return 2 * 6371 * Math.asin(Math.sqrt(s));
  }

  function initialise(root) {
    if (!root || roots.has(root)) return;

    const form = root.querySelector('[data-store-finder-search]');
    const input = form?.querySelector('input[type="search"]');
    const cards = [...root.querySelectorAll('[data-partner-card]')];
    const grid = root.querySelector('[data-store-finder-results]');
    const count = root.querySelector('[data-store-finder-count]');
    const empty = root.querySelector('[data-store-finder-empty]');
    const clear = root.querySelector('[data-store-finder-clear]');
    const locate = root.querySelector('[data-store-finder-locate]');
    const locateStatus = root.querySelector('[data-store-finder-locate-status]');
    const areaLinks = [...root.querySelectorAll('[data-store-finder-area]')];
    if (!form || !input || !cards.length) return;

    roots.add(root);

    const originalOrder = [...cards];
    let sorted = false;
    let userMarker = null;
    let activeCountry = null;

    const setStatus = (text) => {
      if (locateStatus) locateStatus.textContent = text;
    };

    // Country filter (Browse by area): each card carries data-country (GB/IE,
    // from the store_partner metaobject's country_code field). Combines with
    // the text query (both must match) rather than replacing it.
    const filter = () => {
      const query = input.value.trim().toLocaleLowerCase('en-GB');
      let visible = 0;

      cards.forEach((card) => {
        const countryMatch = !activeCountry || card.dataset.country === activeCountry;
        const textMatch = !query || (card.dataset.searchIndex || '').includes(query);
        const match = countryMatch && textMatch;
        card.hidden = !match;
        if (match) visible += 1;
      });

      if (count) count.textContent = `${visible} ${visible === 1 ? 'listing' : 'listings'}`;
      if (empty) empty.hidden = visible !== 0;
      if (clear) clear.hidden = !query && !sorted && !activeCountry;
    };

    areaLinks.forEach((link) => {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        activeCountry = link.dataset.storeFinderArea || null;
        input.value = '';
        filter();
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        grid?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      });
    });

    const resetOrder = () => {
      if (grid) originalOrder.forEach((card) => grid.appendChild(card));
      cards.forEach((card) => {
        const badge = card.querySelector('[data-card-distance]');
        if (badge) {
          badge.textContent = '';
          badge.hidden = true;
        }
      });
      sorted = false;
      markers.forEach((marker) => marker.setIcon(pinIcon(false)));
      if (userMarker && mapInstance) {
        mapInstance.removeLayer(userMarker);
        userMarker = null;
      }
    };

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      filter();
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      root.querySelector('[data-store-finder-results]')?.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'start'
      });
    });

    input.addEventListener('input', filter);

    clear?.addEventListener('click', () => {
      input.value = '';
      activeCountry = null;
      resetOrder();
      setStatus('');
      filter();
      input.focus();
    });

    // Optional geolocation: nearest-first sort by straight-line distance to each
    // listing's town. Runs only after an explicit button press, never on load.
    locate?.addEventListener('click', () => {
      if (!('geolocation' in navigator)) {
        setStatus('Location is not available in this browser. You can still search by town or browse the list below.');
        return;
      }

      locate.disabled = true;
      setStatus('Locating…');

      navigator.geolocation.getCurrentPosition(
        (position) => {
          locate.disabled = false;
          const here = { lat: position.coords.latitude, lng: position.coords.longitude };
          const withDistance = cards.map((card) => {
            const lat = Number(card.dataset.lat);
            const lng = Number(card.dataset.lng);
            const km = Number.isFinite(lat) && Number.isFinite(lng) ? haversineKm(here, { lat, lng }) : null;
            return { card, km };
          });

          withDistance.sort((a, b) => (a.km ?? Infinity) - (b.km ?? Infinity));
          if (grid) withDistance.forEach(({ card }) => grid.appendChild(card));

          withDistance.forEach(({ card, km }) => {
            const badge = card.querySelector('[data-card-distance]');
            if (badge && km != null) {
              badge.textContent = km < 1
                ? 'Under 1 km away (to the town centre)'
                : `About ${Math.round(km)} km away (to the town centre)`;
              badge.hidden = false;
            }
          });

          sorted = true;
          markers.forEach((marker) => marker.setIcon(pinIcon(false)));
          const nearestPin = withDistance.find(({ km }) => km != null);
          if (nearestPin && markers.has(nearestPin.card.dataset.handle)) {
            markers.get(nearestPin.card.dataset.handle).setIcon(pinIcon(true));
          }

          if (mapInstance) {
            if (userMarker) {
              userMarker.setLatLng([here.lat, here.lng]);
            } else {
              userMarker = L.marker([here.lat, here.lng], { icon: userIcon(), title: 'Your approximate location' }).addTo(mapInstance);
            }
            mapInstance.setView([here.lat, here.lng], Math.max(mapInstance.getZoom(), 10));
          }

          setStatus('Sorted by straight-line distance to each listing\'s town. Confirm the exact address with the stockist before travelling.');
          filter();
          if (clear) clear.hidden = false;
        },
        () => {
          locate.disabled = false;
          setStatus('Location unavailable. You can still search by town or browse the list below.');
        },
        { timeout: 9000, maximumAge: 300000 }
      );
    });

    // Live map: Leaflet + OpenStreetMap. Markers open a small preview popup
    // with a link through to the full listing. Lazy-initialised on view.
    const liveMap = root.querySelector('[data-store-finder-live-map]');
    const pinsEl = root.querySelector('[data-store-finder-pins]');
    let mapInstance = null;
    const markers = new Map();

    const escapeHtml = (value) => String(value)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

    const popupHtml = (pin) => `
      <div class="mm-map-popup">
        <strong>${escapeHtml(pin.name)}</strong>
        <span>${escapeHtml(pin.location)}</span>
        <em>${escapeHtml(pin.status)}</em>
        ${pin.url ? `<a href="${escapeHtml(pin.url)}">View partner page</a>` : ''}
      </div>`;

    const pinIcon = (active = false) => L.divIcon({
      className: `mm-map-pin${active ? ' mm-map-pin--active' : ''}`,
      iconSize: [16, 16],
      iconAnchor: [8, 8],
      popupAnchor: [0, -10]
    });

    const userIcon = () => L.divIcon({
      className: 'mm-map-user',
      iconSize: [18, 18],
      iconAnchor: [9, 9]
    });

    const initMap = () => {
      if (mapInstance || !liveMap || !pinsEl || typeof L === 'undefined') return;

      let pins = [];
      try {
        pins = JSON.parse(pinsEl.textContent);
      } catch {
        return;
      }

      mapInstance = L.map(liveMap, {
        scrollWheelZoom: false,
        minZoom: 6,
        maxZoom: 17,
        keyboard: true
      });

      L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap contributors</a>'
      }).addTo(mapInstance);

      const bounds = [];
      pins.forEach((pin) => {
        const marker = L.marker([pin.lat, pin.lng], { icon: pinIcon(), title: pin.name });
        marker.bindPopup(popupHtml(pin), { closeButton: true });
        marker.addTo(mapInstance);
        markers.set(pin.handle, marker);
        bounds.push([pin.lat, pin.lng]);
      });

      if (bounds.length) mapInstance.fitBounds(bounds, { padding: [40, 40] });

      // Scroll zoom only after the visitor interacts with the map, so the page
      // never traps a normal page scroll.
      const enableZoom = () => mapInstance.scrollWheelZoom.enable();
      mapInstance.on('click', enableZoom);
      mapInstance.on('focus', enableZoom);
    };

    if (liveMap && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            initMap();
            observer.disconnect();
          }
        });
      }, { rootMargin: '240px' });
      observer.observe(liveMap);
    } else if (liveMap) {
      initMap();
    }
  }

  function initialiseAll(scope = document) {
    scope.querySelectorAll('[data-store-finder]').forEach(initialise);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialiseAll());
  } else {
    initialiseAll();
  }

  document.addEventListener('shopify:section:load', (event) => initialiseAll(event.target));
})();
